import jsPDF from 'jspdf';
import { downloadBlob } from '@/utils/download';

/**
 * Markdown → PDF for a review's AI summary.
 *
 * This is a hand-rolled renderer on top of jsPDF: it walks the markdown line by
 * line and lays out headings, nested lists, paragraphs and GFM pipe tables
 * itself, paginating as it goes. It is deliberately lifted out of the review
 * detail view **unchanged** rather than rewritten — the pagination arithmetic,
 * the per-level list indents and the table row-height pre-pass are the kind of
 * thing nobody re-derives correctly from a design mock.
 *
 * jsPDF has no rich-text mode, so inline markdown (`**bold**`, `*italic*`,
 * `` `code` ``, `[links](…)`) is stripped rather than styled.
 */

const MARGIN = 40;
const LINE_HEIGHT = 14;
/** Distance from the page bottom at which we break to a new page. */
const PAGE_BREAK_GUTTER = 40;

/** Drop inline markdown that jsPDF cannot render, keeping the visible text. */
function stripInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .trim();
}

/**
 * Draw one GFM table and return the y position below it.
 *
 * Row heights are measured in a first pass so the header band, the alternating
 * row fills and the surrounding border can all be drawn at the right size —
 * jsPDF has no table primitive and no way to grow a cell after the fact.
 */
function renderTable(
  doc: jsPDF,
  headers: string[],
  rows: string[][],
  margin: number,
  yPos: number,
  maxWidth: number,
  pageHeight: number
): number {
  if (headers.length === 0) return yPos;

  const cellPadding = 8;
  const tableWidth = maxWidth;
  const cellWidth = tableWidth / headers.length;
  const tableLineHeight = 12; // Slightly smaller for tables

  doc.setFontSize(9);

  const rowHeights: number[] = [];

  let maxHeaderLines = 1;
  for (const header of headers) {
    const lines = doc.splitTextToSize(header, cellWidth - cellPadding * 2);
    maxHeaderLines = Math.max(maxHeaderLines, lines.length);
  }
  const headerHeight = maxHeaderLines * tableLineHeight + 8;

  for (const row of rows) {
    let maxLines = 1;
    for (let i = 0; i < Math.min(headers.length, row.length); i++) {
      const cellText = row[i] || '';
      const lines = doc.splitTextToSize(cellText, cellWidth - cellPadding * 2);
      maxLines = Math.max(maxLines, lines.length);
    }
    rowHeights.push(maxLines * tableLineHeight + 8);
  }

  doc.setFillColor(240, 240, 240);
  doc.rect(margin, yPos - 10, tableWidth, headerHeight, 'F');

  doc.setFont('helvetica', 'bold');
  for (const [i, header] of headers.entries()) {
    const x = margin + i * cellWidth + cellPadding;
    const text = doc.splitTextToSize(header, cellWidth - cellPadding * 2);
    for (const [j, element] of text.entries()) {
      doc.text(element, x, yPos + j * tableLineHeight);
    }
  }

  yPos += headerHeight;

  doc.setFont('helvetica', 'normal');
  for (const [rowIndex, row] of rows.entries()) {
    const rowHeight = rowHeights[rowIndex];

    if (yPos > pageHeight - PAGE_BREAK_GUTTER) {
      doc.addPage();
      yPos = MARGIN;
    }

    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 248, 248);
      doc.rect(margin, yPos - 10, tableWidth, rowHeight, 'F');
    }

    for (let i = 0; i < Math.min(headers.length, row.length); i++) {
      const x = margin + i * cellWidth + cellPadding;
      const text = doc.splitTextToSize(
        stripInlineMarkdown(row[i] || ''),
        cellWidth - cellPadding * 2
      );
      for (const [j, element] of text.entries()) {
        doc.text(element, x, yPos + j * tableLineHeight);
      }
    }

    yPos += rowHeight;
  }

  doc.setDrawColor(200, 200, 200);
  let totalHeight = headerHeight;
  for (const height of rowHeights) {
    totalHeight += height;
  }
  doc.rect(margin, yPos - totalHeight, tableWidth, totalHeight, 'S');

  doc.setFontSize(11);

  return yPos + 8;
}

/** Render the summary markdown to an A4 PDF and save it as `<fileName>.pdf`. */
export function exportSummaryPdf(summary: string, fileName: string): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });

  doc.setProperties({
    title: 'Contract Review Summary',
    subject: 'AI-Generated Contract Analysis',
    author: 'GAIA Insights',
    creator: 'GAIA Insights',
  });

  doc.setFont('helvetica');
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Contract Review Summary', MARGIN, MARGIN);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'italic');
  doc.text(`Generated on ${new Date().toLocaleDateString()}`, MARGIN, 60);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');

  const lines = summary.split('\n');
  let yPosition = 80;
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = doc.internal.pageSize.getWidth() - MARGIN * 2;

  let inTable = false;
  let tableRows: string[][] = [];
  let tableHeaders: string[] = [];

  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (yPosition > pageHeight - PAGE_BREAK_GUTTER) {
      doc.addPage();
      yPosition = MARGIN;
    }

    // A pipe table: buffer its rows, then draw the whole thing once the next
    // line is no longer part of it (heights can only be measured as a block).
    if (line.includes('|') && line.split('|').length > 2) {
      if (!inTable) {
        inTable = true;
        tableRows = [];
        tableHeaders = [];
      }

      const cells = line
        .split('|')
        .map(cell => cell.trim())
        .filter(Boolean);

      // The |---|---| separator carries no content.
      if (/^[\s\-|]+$/.test(line)) {
        continue;
      }

      if (tableHeaders.length === 0) {
        tableHeaders = cells;
      } else {
        tableRows.push(cells);
      }

      const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
      if (!nextLine.includes('|') || nextLine.split('|').length <= 2) {
        yPosition = renderTable(
          doc,
          tableHeaders,
          tableRows,
          MARGIN,
          yPosition,
          maxWidth,
          pageHeight
        );
        inTable = false;
        tableRows = [];
        tableHeaders = [];
      }
      continue;
    } else if (inTable) {
      yPosition = renderTable(
        doc,
        tableHeaders,
        tableRows,
        MARGIN,
        yPosition,
        maxWidth,
        pageHeight
      );
      inTable = false;
      tableRows = [];
      tableHeaders = [];
    }

    if (line.startsWith('#')) {
      const headingMatch = /^#+/.exec(line);
      const headingLevel = headingMatch ? headingMatch[0].length : 1;
      const headingText = line.replace(/^#+\s*/, '');

      doc.setFontSize(14 - headingLevel); // H1=13, H2=12, H3=11, …
      doc.setFont('helvetica', 'bold');
      doc.text(headingText, MARGIN, yPosition);
      yPosition += LINE_HEIGHT + 4;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      inList = false;
      continue;
    }

    const listMatch = /^(\s*)([-*+]|\d+\.)\s/.exec(line);
    if (listMatch) {
      const indentLevel = Math.floor(listMatch[1].length / 2); // 2 spaces per level
      const listMarker = listMatch[2];
      const listText = line.replace(/^(\s*)([-*+]|\d+\.)\s/, '');

      const bullet = /\d+/.test(listMarker) ? `${listMarker} ` : '• ';
      const indent = 20 + indentLevel * 15;
      const fullText = bullet + stripInlineMarkdown(listText);
      const wrapped = doc.splitTextToSize(fullText, maxWidth - indent);

      for (const textLine of wrapped) {
        if (yPosition > pageHeight - PAGE_BREAK_GUTTER) {
          doc.addPage();
          yPosition = MARGIN;
        }
        doc.text(textLine, MARGIN + indent, yPosition);
        yPosition += LINE_HEIGHT;
      }
      yPosition += 2;
      inList = true;
      continue;
    } else if (inList && line === '') {
      yPosition += 4;
      continue;
    } else if (inList && !line.startsWith(' ') && line !== '') {
      inList = false;
      yPosition += 4;
    }

    if (line === '') {
      yPosition += 4;
    } else {
      const wrapped = doc.splitTextToSize(stripInlineMarkdown(line), maxWidth);
      for (const textLine of wrapped) {
        if (yPosition > pageHeight - PAGE_BREAK_GUTTER) {
          doc.addPage();
          yPosition = MARGIN;
        }
        doc.text(textLine, MARGIN, yPosition);
        yPosition += LINE_HEIGHT;
      }
      yPosition += 4;
    }
  }

  doc.save(`${fileName}.pdf`);
}

/** Save the summary markdown as-is, as `<fileName>.md`. */
export function exportSummaryMarkdown(summary: string, fileName: string): void {
  downloadBlob(new Blob([summary], { type: 'text/markdown' }), `${fileName}.md`);
}
